import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import type { ActivityHit, FacilityHit, PersonHit } from "@/components/AlgoliaSearch";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import {
  navigableGlobalSearchHits,
  toGlobalSearchActivityHit,
  toGlobalSearchFacilityHit,
  toGlobalSearchJobHit,
  toGlobalSearchPersonHit,
  type GlobalSearchEntityHit,
  type JobHit
} from "./mapGlobalSearchHits";
import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  GLOBAL_SEARCH_QUERY,
  GLOBAL_SEARCH_RECENTLY_VIEWED_IDS_PER_TYPE,
  type GlobalSearchHitNode,
  type GlobalSearchTypeFilter
} from "./queries";
import type { GlobalSearchRecentsFilter } from "./globalSearchPanes";

const EMPTY_HITS: GlobalSearchEntityHit[] = [];
const EMPTY_PEOPLE: PersonHit[] = [];
const EMPTY_FACILITIES: FacilityHit[] = [];
const EMPTY_JOBS: JobHit[] = [];
const EMPTY_ACTIVITIES: ActivityHit[] = [];
const EMPTY_RECENTLY_VIEWED_IDS: GlobalSearchRecentlyViewedIds = {
  people: [],
  facilities: [],
  jobs: [],
  activities: []
};
const IDLE_RESULT: GlobalSearchEntitySearch = {
  allHits: EMPTY_HITS,
  peopleHits: EMPTY_PEOPLE,
  facilityHits: EMPTY_FACILITIES,
  jobHits: EMPTY_JOBS,
  activityHits: EMPTY_ACTIVITIES,
  pending: false,
  failed: false
};

export type GlobalSearchRecentlyViewedIds = {
  people: readonly string[];
  facilities: readonly string[];
  jobs: readonly string[];
  activities: readonly string[];
};

export type GlobalSearchEntitySearch = {
  allHits: GlobalSearchEntityHit[];
  peopleHits: PersonHit[];
  facilityHits: FacilityHit[];
  jobHits: JobHit[];
  activityHits: ActivityHit[];
  pending: boolean;
  failed: boolean;
};

function toGlobalSearchHit(node: GlobalSearchHitNode): GlobalSearchEntityHit {
  switch (node.__typename) {
    case "BackofficePerson":
      return toGlobalSearchPersonHit(node);
    case "BackofficeFacility":
      return toGlobalSearchFacilityHit(node);
    case "BackofficeJobPost":
      return toGlobalSearchJobHit(node);
    case "Activity":
      return toGlobalSearchActivityHit(node);
  }
}

function mapNodes(nodes: readonly GlobalSearchHitNode[]): Omit<GlobalSearchEntitySearch, "pending" | "failed"> {
  const allHits = navigableGlobalSearchHits(nodes.map(toGlobalSearchHit));
  return {
    allHits,
    peopleHits: allHits.filter((hit): hit is PersonHit => hit.object_type === "Person"),
    facilityHits: allHits.filter((hit): hit is FacilityHit => hit.object_type === "Facility"),
    jobHits: allHits.filter((hit): hit is JobHit => hit.object_type === "Job"),
    activityHits: allHits.filter((hit): hit is ActivityHit => hit.object_type === "Activity")
  };
}

const FILTER_TO_TYPES: Record<Exclude<GlobalSearchRecentsFilter, "all">, GlobalSearchTypeFilter> = {
  people: "PEOPLE",
  facilities: "FACILITIES",
  jobs: "JOBS",
  activities: "ACTIVITIES"
};

export function globalSearchTypesForFilter(filter: GlobalSearchRecentsFilter): GlobalSearchTypeFilter[] | undefined {
  if (filter === "all") return undefined;
  return [FILTER_TO_TYPES[filter]];
}

function cappedIds(ids: readonly string[]): string[] {
  return (ids as string[]).slice(0, GLOBAL_SEARCH_RECENTLY_VIEWED_IDS_PER_TYPE);
}

export function useGlobalSearchEntitySearch(
  query: string,
  enabled: boolean,
  recentlyViewedIds: GlobalSearchRecentlyViewedIds = EMPTY_RECENTLY_VIEWED_IDS,
  filter: GlobalSearchRecentsFilter = "all"
): GlobalSearchEntitySearch {
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const staleRef = useRef<GlobalSearchEntitySearch>(IDLE_RESULT);

  useEffect(() => {
    if (!enabled || query.trim().length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
      // Idle (closed, cleared, or below the minimum): drop the debounced
      // query and any stale hits so the next search never paints the
      // previous query's rows while loading.
      setDebouncedQuery("");
      staleRef.current = IDLE_RESULT;
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), GLOBAL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, query]);

  const trimmed = debouncedQuery.trim();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewJobs = useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.viewJobPosts });
  const canManageJobs = useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.manageJobPosts });
  const canViewActivities = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.manageEvents
  });

  const canSearchAny = canViewPeople || canViewFacilities || canViewJobs || canManageJobs || canViewActivities;
  const queryReady = enabled && trimmed.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH && canSearchAny;
  const types = globalSearchTypesForFilter(filter);
  const searchQuery = useQuery(GLOBAL_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      firstPerType: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      types,
      recentlyViewedIds: {
        people: cappedIds(recentlyViewedIds.people),
        facilities: cappedIds(recentlyViewedIds.facilities),
        jobs: cappedIds(recentlyViewedIds.jobs),
        activities: cappedIds(recentlyViewedIds.activities)
      },
      withCareer: canViewPersonCareer
    },
    skip: !queryReady,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const fresh = useMemo(() => mapNodes(searchQuery.data?.globalSearch ?? []), [searchQuery.data]);

  useEffect(() => {
    if (queryReady && !searchQuery.loading && searchQuery.data) {
      staleRef.current = { ...fresh, pending: false, failed: Boolean(searchQuery.error) && fresh.allHits.length === 0 };
    }
  }, [fresh, queryReady, searchQuery.data, searchQuery.error, searchQuery.loading]);

  if (!queryReady) {
    // Debounce in flight with no usable debounced query yet: report pending
    // so the CCMS lookup CTA does not flash before entity search runs.
    const awaitingDebounce =
      enabled && query.trim().length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH && query.trim() !== debouncedQuery.trim();
    return awaitingDebounce ? { ...IDLE_RESULT, pending: true } : IDLE_RESULT;
  }
  if (searchQuery.loading) {
    const stale = staleRef.current;
    // Stale-while-revalidate: keep the last loaded hits on screen while the
    // debounced query (or a filter change) is in flight.
    return stale === IDLE_RESULT ? { ...IDLE_RESULT, pending: true } : { ...stale, pending: true, failed: false };
  }
  const awaitingDebounce = enabled && query.trim().length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH && query.trim() !== trimmed;
  return {
    ...fresh,
    pending: awaitingDebounce,
    failed: Boolean(searchQuery.error) && fresh.allHits.length === 0
  };
}
